import { ReactNode } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { Tabs as AntdTabs, TabsProps as AntTabsProps } from '@/shared/uikitV2/antd/Tabs';

export * from './TabLabel';

type TabItem<T> = {
  label: ReactNode;
  key: T;
};

export type TabsProps<T> = AntTabsProps & {
  defaultValue?: string;
  items: TabItem<T>[];
  onChange: (value: T) => void;
  fullWidth?: boolean;
  scrollable?: boolean;
};

export const Tabs = <T,>(props: TabsProps<T>) => {
  const { defaultValue, items, onChange, className, fullWidth = false, scrollable = false } = props;

  return (
    <TabsStyled
      {...props}
      className={classNames(className, { ['full-width']: fullWidth, scrollable })}
      defaultActiveKey={defaultValue}
      items={items}
      onChange={onChange}
    />
  );
};

export const TabsStyled = styled(AntdTabs)`
  &&&.ant-tabs {
    height: 32px;
    overflow: hidden;
  }

  &&& .ant-tabs-ink-bar {
    height: 1px;
    background: ${COLORS.systemSky200};
  }

  &&& .ant-tabs-nav-list {
    height: 32px;
    width: 100%;
  }

  &&&.full-width .ant-tabs-nav-list {
    width: 100%;
  }

  &&& .ant-tabs-tab {
    margin: 0;
    padding-inline: 16px;
    justify-content: center;
  }

  &&&.full-width .ant-tabs-tab {
    flex: 1;
    justify-content: center;
  }

  &&& .ant-tabs-nav:before {
    border: none;
  }

  &&&.scrollable .ant-tabs-nav-wrap {
    overflow-x: auto;
    touch-action: pan-x;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &::before,
    &::after {
      display: none;
    }
  }

  &&&.scrollable .ant-tabs-nav-list {
    width: max-content;
  }

  &&&.scrollable .ant-tabs-nav-operations {
    display: none;
  }
`;
